import React, {useEffect, useMemo, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {
    createReferralWithdraw,
    createReferralWithdrawFailure,
    createWithdraw,
    createWithdrawFailure,
    getDashboard,
    getReferrals,
    getReferralWithdrawInfo,
    getWithdrawInfo,
    setReferralWithdrawModalStatus,
    setWithdrawModalStatus
} from "../../../store/cabinet/action";
import {amountHandler, dateTimeHandler} from "../../../helpers/capsule";

// Внутренний баланс приходит без валюты — тикер берём из макета
const BALANCE_CURRENCY = '$TC';

const tokenAmountHandler = (value) => {
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0) {
        return '0';
    }
    return String(Number(num.toFixed(6)));
};

const shortAddressHandler = (address) => {
    if (!address) {
        return '';
    }
    return `${address.slice(0, 6)}…${address.slice(-4)}`;
};

// isReferral — тот же вывод, но реферального баланса: с API 22 это отдельные
// ручки, своя очередь у админов и свой кошелёк. Форма и шаги совпадают,
// поэтому компонент один, а источники данных подменяются флагом
const CabinetMainWithdrawModal = ({isReferral = false}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const withdrawModalStatus = useSelector(store => (
        isReferral ? store.cabinet.referralWithdrawModalStatus : store.cabinet.withdrawModalStatus
    ));
    const info = useSelector(store => (
        isReferral ? store.cabinet.referralWithdrawInfo : store.cabinet.withdrawInfo
    ));
    const infoErr = useSelector(store => (
        isReferral ? store.cabinet.referralWithdrawInfoErr : store.cabinet.withdrawInfoErr
    ));
    const infoStarted = useSelector(store => (
        isReferral ? store.cabinet.referralWithdrawInfoStarted : store.cabinet.withdrawInfoStarted
    ));
    const createStarted = useSelector(store => (
        isReferral ? store.cabinet.createReferralWithdrawStarted : store.cabinet.createWithdrawStarted
    ));
    const formErr = useSelector(store => (
        isReferral ? store.cabinet.createReferralWithdrawErr : store.cabinet.createWithdrawErr
    ));
    const withdraw = useSelector(store => (
        isReferral ? store.cabinet.createReferralWithdrawStatus : store.cabinet.createWithdrawStatus
    ));

    // form -> done
    const [step, setStep] = useState('form');
    const [amount, setAmount] = useState('');
    const [wallet, setWallet] = useState('');

    // 503 — вывод отключён на сервере, форму показывать нечем
    const isUnavailable = !!infoErr && !info;

    const network = useMemo(()=>{
        if (!info){
            return '';
        }
        const chain = info?.chain === 'bsc' ? 'BEP-20' : String(info?.chain || '').toUpperCase();
        return chain;
    }, [info])

    useEffect(()=>{
        if (withdrawModalStatus){
            dispatch(isReferral ? getReferralWithdrawInfo() : getWithdrawInfo());
        }
    },[withdrawModalStatus, isReferral, dispatch])

    // Токенов на кошелёк = (сумма − комиссия) / курс
    const tokenAmount = useMemo(()=>{
        const value = Number(String(amount).replace(',', '.'));
        const fee = Number(info?.fee || 0);
        const rate = Number(info?.rate || 0);

        if (!info || !String(amount).trim().length || Number.isNaN(value) || !rate){
            return '';
        }

        const result = (value - fee) / rate;
        // до ввода суммы (и при сумме ниже комиссии) в макете стоит прочерк
        return result > 0 ? tokenAmountHandler(result) : '';
    },[amount, info])

    const closeModal = () => {
        dispatch(isReferral ? setReferralWithdrawModalStatus(false) : setWithdrawModalStatus(false));
        dispatch(isReferral ? createReferralWithdrawFailure(false) : createWithdrawFailure(false));
        setStep('form');
        setAmount('');
        setWallet('');
    };

    const createHandler = () => {
        if (isUnavailable || createStarted || !amount.trim().length || !wallet.trim().length){
            return;
        }

        const body = {
            amount: String(amount).replace(',', '.').trim(),
            wallet_address: wallet.trim()
        };

        dispatch(isReferral ? createReferralWithdrawFailure(false) : createWithdrawFailure(false));
        dispatch(isReferral
            ? createReferralWithdraw(body, ()=>{
                // реферальный баланс списывается сразу — обновляем карточку наград
                dispatch(getReferrals());
                dispatch(getDashboard());
                setStep('done');
            })
            : createWithdraw(body, ()=>{
                // баланс списывается сразу — обновляем главную
                dispatch(getDashboard());
                setStep('done');
            }));
    };

    const commonErr = (formErr && !formErr?.errors)
        ? (formErr?.message || t('Не удалось создать заявку'))
        : '';

    return (
        <Popup
            open={withdrawModalStatus}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                <div className={`${Styles.modalContentWrap}`}>
                    <div className={`${Styles.modalHead}`}>
                        <div className={`${Styles.modalTitle}`}>
                            {isReferral ? t('Вывод вознаграждений') : t('Вывод средств')}
                        </div>
                        <div className={`${Styles.modalNote}`}>
                            {step === 'done'
                                ? t('Заявка принята — средства спишутся с баланса и уйдут после проверки')
                                : t('Укажите сумму и кошелёк, на который получить средства')}
                        </div>
                    </div>

                    <div className={`${Styles.modalBody}`}>
                        {isUnavailable ? (
                            <div className={`${Styles.emptyNote}`}>
                                {t('Вывод средств временно недоступен. Попробуйте позже.')}
                            </div>
                        ) : null}

                        {!isUnavailable && step === 'form' ? (
                            <div className={`${Styles.formWrap}`}>
                                <div className={`${Styles.balanceBox}`}>
                                    <span className={`${Styles.balanceName}`}>
                                        {isReferral ? t('Доступно к выводу') : t('Доступно на балансе')}
                                    </span>
                                    <span className={`${Styles.balanceValue}`}>
                                        {infoStarted && !info ? '—' : `${amountHandler(info?.balance)} ${BALANCE_CURRENCY}`}
                                    </span>
                                </div>

                                <div className={`${Styles.fieldBox}`}>
                                    <div className={`${Styles.fieldName}`}>{t('Сумма вывода')}</div>
                                    <div className={`${Styles.fieldInputWrap} ${formErr?.errors?.amount ? Styles.fieldInputWrapErr : ''}`}>
                                        <input
                                            className={`${Styles.fieldInput}`}
                                            type='number'
                                            inputMode='decimal'
                                            placeholder='0'
                                            value={amount}
                                            onChange={(e)=>{
                                                setAmount(e.target.value);
                                                if (formErr){
                                                    dispatch(createWithdrawFailure(false));
                                                }
                                            }}
                                        />
                                        <span className={`${Styles.fieldChip}`}>{BALANCE_CURRENCY}</span>
                                    </div>

                                    <div className={`${Styles.fieldHints}`}>
                                        <span>
                                            {t('Минимум')} {amountHandler(info?.min_amount)} {BALANCE_CURRENCY}
                                        </span>
                                        <span>
                                            {t('Максимум')} {amountHandler(info?.max_amount)} {BALANCE_CURRENCY}
                                        </span>
                                    </div>

                                    {formErr?.errors?.amount ? (
                                        <div className={`${Styles.tcErr} ${Styles.tcErrActive}`}>
                                            {formErr.errors.amount[0]}
                                        </div>
                                    ) : null}
                                </div>

                                <div className={`${Styles.fieldBox}`}>
                                    <div className={`${Styles.fieldNameRow}`}>
                                        <span className={`${Styles.fieldName}`}>
                                            {t('Кошелёк, на который получить средства')}
                                        </span>
                                        {network ? (
                                            <span className={`${Styles.netChip}`}>{t('Сеть')} {network}</span>
                                        ) : null}
                                    </div>
                                    <div className={`${Styles.fieldInputWrap} ${formErr?.errors?.wallet_address ? Styles.fieldInputWrapErr : ''}`}>
                                        <input
                                            className={`${Styles.fieldInput}`}
                                            type='text'
                                            placeholder='0x...'
                                            value={wallet}
                                            onChange={(e)=>{
                                                setWallet(e.target.value);
                                                if (formErr){
                                                    dispatch(createWithdrawFailure(false));
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className={`${Styles.fieldHint}`}>
                                        {t('Проверьте адрес и сеть — перевод в другую сеть вернуть нельзя')}
                                    </div>

                                    {formErr?.errors?.wallet_address ? (
                                        <div className={`${Styles.tcErr} ${Styles.tcErrActive}`}>
                                            {formErr.errors.wallet_address[0]}
                                        </div>
                                    ) : null}
                                </div>

                                <div className={`${Styles.summaryWrap}`}>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Срок обработки')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {t('после проверки администратором')}
                                        </span>
                                    </div>
                                    {Number(info?.fee) ? (
                                        <>
                                            <div className={`${Styles.summaryDivider}`}/>
                                            <div className={`${Styles.summaryRow}`}>
                                                <span>{t('Комиссия')}</span>
                                                <span className={`${Styles.summaryValue}`}>
                                                    {amountHandler(info?.fee)} {BALANCE_CURRENCY}
                                                </span>
                                            </div>
                                        </>
                                    ) : null}
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Зачисление на кошелёк')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {tokenAmount ? `${tokenAmount} ${info?.token?.symbol || ''}` : '—'}
                                        </span>
                                    </div>
                                </div>

                                <div className={`${Styles.tcErr} ${commonErr ? Styles.tcErrActive : ''}`}>{commonErr}</div>
                            </div>
                        ) : null}

                        {!isUnavailable && step === 'done' ? (
                            <div className={`${Styles.formWrap}`}>
                                <div className={`${Styles.statusWrap}`}>
                                    <span className={`${Styles.statusMark}`}>✓</span>
                                    <span className={`${Styles.statusPos}`}>
                                        <span className={`${Styles.statusTitle}`}>{t('Заявка на вывод создана')}</span>
                                        <span className={`${Styles.statusNote}`}>
                                            {t('Мы пришлём уведомление, когда перевод будет отправлен')}
                                        </span>
                                    </span>
                                </div>

                                <div className={`${Styles.summaryWrap}`}>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Сумма списания')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {amountHandler(withdraw?.amount)} {BALANCE_CURRENCY}
                                        </span>
                                    </div>
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Зачисление на кошелёк')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {tokenAmountHandler(withdraw?.token_amount)} {withdraw?.token_symbol || ''}
                                        </span>
                                    </div>
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Кошелёк')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {shortAddressHandler(withdraw?.wallet_address)}
                                        </span>
                                    </div>
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Создана')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            {dateTimeHandler(withdraw?.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className={`${Styles.modalFooter}`}>
                        {step === 'form' ? (
                            <>
                                <div className={`${Styles.modalBtnGhost}`} onClick={closeModal}>{t('Отмена')}</div>
                                <div
                                    className={`${Styles.modalBtn} ${isUnavailable || createStarted || !amount.trim().length || !wallet.trim().length ? Styles.modalBtnDsb : ''}`}
                                    onClick={createHandler}
                                >
                                    {createStarted ? t('Создаём заявку…') : t('Вывести')}
                                </div>
                            </>
                        ) : (
                            <div className={`${Styles.modalBtn} ${Styles.modalBtnWide}`} onClick={closeModal}>
                                {t('Готово')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetMainWithdrawModal;
